import { supabase } from '../client'
import type { CloudGoal } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── GOALS (Cloud) ─────────────────────────────────────────── */

/**
 * Get all goals for user. Ordered newest-first to match the Ziele page.
 */
export async function getCloudGoals(userId: string, since?: Date): Promise<CloudGoal[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('goals')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })

    if (since) {
      query = query.gte('updated_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      logSupabaseError('Get goals error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get goals error', error)
    return []
  }
}

/**
 * Get all cloud goal IDs for a user (lightweight — only fetches id column).
 * Used for ID-set reconciliation to detect goals missing from local DB.
 */
export async function getCloudGoalIds(userId: string): Promise<string[]> {
  if (!supabase) return []

  try {
    const { data, error } = await supabase
      .from('goals')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(10000)

    if (error) {
      logSupabaseError('Get goal IDs error', error)
      return []
    }

    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get goal IDs error', error)
    return []
  }
}

/**
 * Get specific cloud goals by their IDs.
 * Used for targeted fetch of goals missing from local DB.
 */
export async function getCloudGoalsByIds(userId: string, ids: string[]): Promise<CloudGoal[]> {
  if (!supabase || ids.length === 0) return []

  try {
    const { data, error } = await supabase
      .from('goals')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)

    if (error) {
      logSupabaseError('Get goals by IDs error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get goals by IDs error', error)
    return []
  }
}

/**
 * Get cloud goals that were deleted since a given date
 */
export async function getDeletedCloudGoals(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('goals')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)

    if (since) {
      query = query.gte('deleted_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      logSupabaseError('Get deleted goals error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get deleted goals error', error)
    return []
  }
}

/**
 * Create a new goal
 */
export async function createCloudGoal(
  userId: string,
  goal: Omit<CloudGoal, 'id' | 'user_id' | 'created_at' | 'updated_at'>
): Promise<CloudGoal | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase
      .from('goals')
      .insert({
        ...goal,
        user_id: userId,
      })
      .select()
      .single()

    if (error) {
      logSupabaseError('Create goal error', error)
      return null
    }

    return data
  } catch (error) {
    logSupabaseError('Create goal error', error)
    return null
  }
}

/**
 * Update a goal. Returns the server-assigned updated_at timestamp on success,
 * or null on failure.
 */
export async function updateCloudGoal(
  goalId: string,
  updates: Partial<Omit<CloudGoal, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null

  try {
    let query = supabase
      .from('goals')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', goalId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query
      .select('updated_at')
      .single()

    if (error) {
      logSupabaseError('Update goal error', error)
      return null
    }

    return data
  } catch (error) {
    logSupabaseError('Update goal error', error)
    return null
  }
}

/**
 * Delete a goal (soft delete)
 */
export async function deleteCloudGoal(goalId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false

  try {
    let query = supabase
      .from('goals')
      .update({
        deleted_at: new Date().toISOString(),
      })
      .eq('id', goalId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query

    if (error) {
      logSupabaseError('Delete goal error', error)
      return false
    }

    return true
  } catch (error) {
    logSupabaseError('Delete goal error', error)
    return false
  }
}
